import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Components
import Page from 'src/components/Page';
import { HeaderPageComponentConfig } from 'src/components/_dashboard/configuracoes';
import TabCrm from 'src/sections/@dashboard/home/TabCrm';

// Shadcn Components
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Badge } from 'src/@/components/ui/badge';
import { Progress } from 'src/@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Button } from 'src/@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';

// Icons
import { TrendingUp, TrendingDown, Eye, MousePointer, DollarSign, Users, BarChart3, PieChart, CalendarIcon, Download } from 'lucide-react';

// Utils
import { fCurrency } from 'src/utils/formatNumber';
import { fDate, fDateTimeSuffix } from 'src/utils/formatTime';

// Hooks
import useAuth from 'src/hooks/useAuth';

// Actions
import v2_actionCampanhaDetalhes from 'src/actions/v2/marketing/v2_actionCampanhaDetalhes';
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';

// ----------------------------------------------------------------------


export default function MarketingMetricas() {
  const { user, cliente } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const campanhaId = searchParams.get('campanha');
  const campanhaNome = searchParams.get('nome');

  // Estados
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [campanhaData, setCampanhaData] = useState(null);
  const [metricasTempoReal, setMetricasTempoReal] = useState(null);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [apiData, setApiData] = useState(null);
  const [canalSelecionado, setCanalSelecionado] = useState(cliente._id);
  const [periodo, setPeriodo] = useState('30');
  const [dadosEstados, setDadosEstados] = useState([]);
  const [chatsCapturados, setChatsCapturados] = useState([]);

  // Buscar dados da campanha
  useEffect(() => {
    if (campanhaId) {
      buscarDadosCampanha();
    }
  }, [campanhaId]);

  // Buscar dados dos estados quando apiData estiver disponível
  useEffect(() => {
    if (apiData) {
      buscarDadosEstados();
    }
  }, [apiData]);

  const buscarDadosCampanha = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await v2_actionCampanhaDetalhes(campanhaId);

      if (response.data.error) {
        throw new Error(response.data.message || 'Erro ao buscar dados da campanha');
      }

      const data = response.data.data;
      setApiData(data);

      // Mapear dados da API para a estrutura existente
      const campanhaMapeada = {
        _id: data.campanha.id,
        nome: campanhaNome ? decodeURIComponent(campanhaNome) : data.campanha.nome,
        plataforma: data.campanha.plataforma,
        status: data.campanha.status === 'active' ? 'ativa' : data.campanha.status,
        descricao: data.campanha.descricao,
        orcamento: parseFloat(data.orcamento.valor),
        gasto: parseFloat(data.orcamento.gastoTotal),
        impressoes: data.metricados?.impressoes?.valor || 0,
        cliques: data.metricados?.capturas?.valor || 0,
        conversoes: data.metricados?.conversaoLeads?.valor || 0,
        chats_criados: data.metricados?.chatsCriados?.valor || 0,
        conversao_chats: data.metricados?.conversaoChats?.valor || 0,
        cpc_metricas: data.metricados?.cpc,
        publico_alvo: data.informacoes.publicoAlvo,
        regioes: data.informacoes.regioesAtuacao,
        horarios_pico: data.informacoes.horariosPico,
        palavras_chave: data.informacoes.palavrasChave && data.informacoes.palavrasChave.length > 0
          ? data.informacoes.palavrasChave
          : [],
        data_inicio: new Date(data.periodo.dataInicio),
        data_fim: new Date(data.periodo.dataFinal),
        dias_restantes: data.periodo.diasRestantes,
        kpis: data.kpis,
        percentual_utilizado: data.orcamento.percentualUtilizado
      };

      setCampanhaData(campanhaMapeada);

      // Métricas em tempo real
      const metricasHoje = {
        impressoes_hoje: data.metricasHoje.impressoes,
        cliques_hoje: data.metricasHoje.cliques,
        conversoes_hoje: data.metricasHoje.conversoes,
        gasto_hoje: parseFloat(data.metricasHoje.gasto.replace('$', '').replace(',', '.')),
        chats_criados_hoje: data.metricados?.chatsCriados?.valor || 0,
        conversao_chats_hoje: data.metricados?.conversaoChats?.valor || 0,
        tendencia_impressoes: data.metricados?.impressoes?.variacao || '0%',
        tendencia_cliques: data.metricados?.capturas?.variacao || '0%',
        tendencia_conversoes: data.metricados?.conversaoLeads?.variacao || '0%',
        tendencia_gasto: data.metricados?.gastoTotal?.variacao || '0%',
        ultima_atualizacao: new Date(data.ultimaAtualizacao)
      };

      setMetricasTempoReal(metricasHoje);

      // Chats capturados
      if (data.chatsCapturados) {
        setChatsCapturados(data.chatsCapturados);
      }

    } catch (error) {
      console.error('Erro ao buscar dados da campanha:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const buscarMetricasTempoReal = async () => {
    try {
      // Simular métricas em tempo real
      const metricas = {
        impressoes_hoje: 1250,
        cliques_hoje: 38,
        conversoes_hoje: 2,
        gasto_hoje: 56.40,
        ctr_hoje: '3.04',
        cpc_hoje: '1.48',
        cpa_hoje: '28.20',
        roas_hoje: '3.55',
        tendencia_impressoes: '+12%',
        tendencia_cliques: '+8%',
        tendencia_conversoes: '+15%',
        tendencia_gasto: '+5%',
        ultima_atualizacao: new Date()
      };

      setMetricasTempoReal(metricas);
    } catch (error) {
      console.log('Erro ao buscar métricas em tempo real:', error);
    }
  };

  const buscarDadosGrafico = async () => {
    try {
      // Dados mock para gráficos (últimos 30 dias)
      const dados = [];
      const hoje = new Date();

      for (let i = 29; i >= 0; i--) {
        const data = new Date(hoje);
        data.setDate(data.getDate() - i);

        dados.push({
          data: data,
          impressoes: Math.floor(Math.random() * 5000) + 2000,
          cliques: Math.floor(Math.random() * 150) + 50,
          conversoes: Math.floor(Math.random() * 8) + 1,
          gasto: Math.floor(Math.random() * 200) + 50,
          ctr: (Math.random() * 2 + 2).toFixed(2),
          cpc: (Math.random() * 1 + 1).toFixed(2),
          cpa: (Math.random() * 30 + 30).toFixed(2),
          roas: (Math.random() * 2 + 2).toFixed(2)
        });
      }

      setDadosGrafico(dados);
    } catch (error) {
      console.log('Erro ao buscar dados do gráfico:', error);
    }
  };

  const buscarDadosEstados = async () => {
    try {
      // Usar dados da API se disponíveis
      if (apiData && apiData.distribuicaoGeografica && apiData.distribuicaoGeografica.dddPorEstado) {
        const estadosApi = apiData.distribuicaoGeografica.dddPorEstado;

        // Mapear dados da API para o formato esperado
        const estados = estadosApi.map((estado, index) => ({
          nome: estado.nome,
          sigla: estado.sigla,
          capturas: estado.capturas || 0,
          cor: estado.cor || `#${Math.floor(Math.random() * 16777215).toString(16)}`
        }));

        // Ordenar por número de capturas (maior para menor)
        estados.sort((a, b) => b.capturas - a.capturas);

        setDadosEstados(estados);
      }
    } catch (error) {
      console.log('Erro ao buscar dados dos estados:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativa': return 'default';
      case 'pausada': return 'secondary';
      case 'finalizada': return 'outline';
      case 'erro': return 'destructive';
      default: return 'secondary';
    }
  };

  const getPlataformaIcon = (plataforma) => {
    switch (plataforma) {
      case 'facebook': return '🎯';
      case 'google': return '🔍';
      case 'instagram': return '📸';
      default: return '📱';
    }
  };

  const getPlataformaColor = (plataforma) => {
    switch (plataforma) {
      case 'facebook': return '#1877F2';
      case 'google': return '#4285F4';
      case 'instagram': return '#E4405F';
      default: return '#666';
    }
  };

  const calcularProgressoOrcamento = () => {
    if (!campanhaData) return 0;
    return campanhaData.percentual_utilizado || (campanhaData.gasto / campanhaData.orcamento) * 100;
  };

  const calcularDiasRestantes = () => {
    if (!campanhaData) return 0;
    return campanhaData.dias_restantes || 0;
  };

  const exportarChatsExcel = () => {
    if (!chatsCapturados.length) return;

    const headers = ['#', 'Número', 'Nome', 'Funil', 'Estágio', 'Data Criação Chat'];
    const rows = chatsCapturados.map((chat, index) => [
      index + 1,
      chat.chat_numero || chat.phone || '',
      chat.chat_nome || '',
      chat.nome_funil || '',
      chat.estagio_funil || '',
      chat.chat_data_criacao ? fDateTimeSuffix(new Date(chat.chat_data_criacao)) : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `chats-capturados-${campanhaData?.nome || 'campanha'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Page title="Métricas da Campanha">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-lg text-muted-foreground">Carregando métricas...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Métricas da Campanha">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <p className="text-lg text-red-600 mb-2">Erro ao carregar dados</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div></div>
      </Page>
    );
  }

  if (!campanhaData) {
    return (
      <Page title="Métricas da Campanha">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <p className="text-lg text-muted-foreground">Campanha não encontrada</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page title={`Métricas da Campanha ${campanhaData.nome}`} className="h-full">
      <div className="container mx-auto px-4 py-4 h-full flex flex-col overflow-hidden">

        {/* Header - Fixed */}
        <div className="flex-none mb-6">
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Botão voltar */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleVoltar}
                className="h-7 w-7 hover:bg-gray-100 flex-shrink-0"
              >
                <Iconify icon={'material-symbols:arrow-back-ios'} height={18} width={18} />
              </Button>

              {/* Título */}
              <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">{campanhaData.nome}</h1>

              {/* Separador */}
              <div className="w-px h-6 bg-gray-200"></div>

              {/* Badges */}
              <Badge
                variant={getStatusColor(campanhaData.status) === 'success' ? 'default' : 'secondary'}
                className="text-xs h-5 whitespace-nowrap"
              >
                {campanhaData.status}
              </Badge>
              <span
                className="text-xs font-medium capitalize px-2 py-0.5 rounded bg-gray-100 whitespace-nowrap"
                style={{ color: getPlataformaColor(campanhaData.plataforma) }}
              >
                {campanhaData.plataforma} Ads
              </span>

              {/* Separador */}
              <div className="w-px h-6 bg-gray-200"></div>

              {/* Datas */}
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {fDate(campanhaData.data_inicio)} - {fDate(campanhaData.data_fim)}
              </span>

              {/* Separador */}
              <div className="w-px h-6 bg-gray-200"></div>

              {/* Métricas */}
              <div className="flex items-center gap-3">
                <div>
                  <span className="text-xs text-gray-500">Orçamento: </span>
                  <span className="text-sm font-semibold text-gray-900">R$ {fCurrency(campanhaData.orcamento)}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Dias restantes: </span>
                  <span className="text-sm font-semibold text-gray-900">{calcularDiasRestantes()}</span>
                </div>
              </div>

              {/* Palavras-chave */}
              {campanhaData.palavras_chave?.length > 0 && (
                <>
                  <div className="w-px h-6 bg-gray-200"></div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      Palavras-chave ({campanhaData.palavras_chave.length}):
                    </span>
                    {campanhaData.palavras_chave.slice(0, 6).map((key, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200 whitespace-nowrap"
                      >
                        {key.value}
                      </span>
                    ))}
                    {campanhaData.palavras_chave.length > 6 && (
                      <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">
                        +{campanhaData.palavras_chave.length - 6} mais
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-20 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pr-2">

          {/* Métricas de Capturas e Análise Geográfica */}
          <div className="mb-8">
            <div className="bg-slate-100 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <h3 className="text-xl font-bold text-gray-800">Métricas de Conversas</h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Métricas de Capturas */}
                <div>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-600 mb-2">Chats Capturados</p>
                            <p className="text-3xl font-bold text-gray-900">
                              {campanhaData.cliques.toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div className="p-4 bg-blue-100 rounded-xl">
                            <MousePointer className="h-8 w-8 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-600 mb-2">CPC (Custo por Captura)</p>
                            <p className="text-3xl font-bold text-gray-900">
                              {campanhaData.cpc_metricas?.valor !== null ? `R$ ${parseFloat(campanhaData.cpc_metricas?.valor).toFixed(2)}` : campanhaData.kpis?.cpc || 'N/A'}
                            </p>
                          </div>
                          <div className="p-4 bg-purple-100 rounded-xl">
                            <Eye className="h-8 w-8 text-purple-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-600 mb-2">Chats Criados</p>
                            <p className="text-3xl font-bold text-gray-900">
                              {campanhaData.chats_criados.toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div className="p-4 bg-green-100 rounded-xl">
                            <Users className="h-8 w-8 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-600 mb-2">Taxa de Conversão</p>
                            <p className="text-3xl font-bold text-gray-900">
                              {campanhaData.conversao_chats !== null && campanhaData.conversao_chats !== undefined
                                ? `${campanhaData.conversao_chats}%`
                                : campanhaData.chats_criados > 0
                                  ? `${((campanhaData.conversoes / campanhaData.chats_criados) * 100).toFixed(1)}%`
                                  : '0%'
                              }
                            </p>
                          </div>
                          <div className="p-4 bg-orange-100 rounded-xl">
                            <TrendingUp className="h-8 w-8 text-orange-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Tabela de Estados */}
                <div>
                  <Card className="border-0 bg-white shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <BarChart3 className="h-6 w-6 text-green-600" />
                        </div>
                        Capturas por Estado
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-[60vh] overflow-y-auto">
                        <Table className="relative">
                          <TableHeader>
                            <TableRow className="border-gray-200">
                              <TableHead className="w-12 text-gray-600 font-semibold sticky top-0 z-10 bg-white">#</TableHead>
                              <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">Estado</TableHead>
                              <TableHead className="text-right text-gray-600 font-semibold sticky top-0 z-10 bg-white">Capturas</TableHead>
                              <TableHead className="text-right text-gray-600 font-semibold sticky top-0 z-10 bg-white">%</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dadosEstados.map((estado, index) => {
                              const totalCapturas = dadosEstados.reduce((sum, e) => sum + e.capturas, 0);
                              const percentual = ((estado.capturas / totalCapturas) * 100).toFixed(1);

                              return (
                                <TableRow key={estado.sigla} className="hover:bg-gray-50 transition-colors">
                                  <TableCell className="font-medium">
                                    <div
                                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
                                      style={{ backgroundColor: estado.cor }}
                                    >
                                      {index + 1}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="font-semibold text-gray-900">{estado.nome}</div>
                                      <div className="text-sm text-gray-500">{estado.sigla}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-bold text-gray-900">
                                    {estado.capturas.toLocaleString('pt-BR')}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-3">
                                      <div className="w-20 bg-gray-200 rounded-full h-2">
                                        <div
                                          className="h-2 rounded-full transition-all duration-500"
                                          style={{
                                            width: `${percentual}%`,
                                            backgroundColor: estado.cor
                                          }}
                                        ></div>
                                      </div>
                                      <span className="text-sm font-semibold text-gray-700 w-10">
                                        {percentual}%
                                      </span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>

        

          {/* Métricas de Leads (CRM) */}
          <div className="mb-6 bg-slate-100 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Métricas de Leads (CRM)
              </h3>
            </div>

            {/* Componente TabCrm */}
            <TabCrm
              changeClienteId={setCanalSelecionado}
              changePeriodo={setPeriodo}
              clienteId={canalSelecionado}
              periodo={periodo}
              hidePeriod={true}
              hidePannels={['last_6_months']}
              campaingConfig={
                {
                  start_date: campanhaData.data_inicio,
                  end_date: campanhaData.data_fim,
                  campaign_id: campanhaData._id
                }
              }
            />
          </div>

            {/* Tabela de Chats Capturados */}
          {chatsCapturados.length > 0 && (
            <div className="mb-8">
              <div className="bg-slate-100 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Chats Capturados</h3>
                    <Badge variant="secondary" className="ml-2">{chatsCapturados.length}</Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportarChatsExcel}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Exportar Excel
                  </Button>
                </div>

                <Card className="border-0 bg-white shadow-sm">
                  <CardContent className="p-0">
                    <div className="max-h-[500px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-200">
                            <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">#</TableHead>
                            <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">Número</TableHead>
                            <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">Nome</TableHead>
                            <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">Funil</TableHead>
                            <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">Estágio</TableHead>
                            <TableHead className="text-gray-600 font-semibold sticky top-0 z-10 bg-white">Data Criação Chat</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {chatsCapturados.map((chat, index) => (
                            <TableRow key={chat._id} className="hover:bg-gray-50 transition-colors">
                              <TableCell className="font-medium text-gray-500">{index + 1}</TableCell>
                              <TableCell className="font-medium text-gray-900">
                                {chat.chat_numero || chat.phone || '-'}
                              </TableCell>
                              <TableCell>
                                <span className="font-semibold text-gray-900">
                                  {chat.chat_nome || '-'}
                                </span>
                              </TableCell>
                              <TableCell>
                                {chat.nome_funil ? (
                                  <Badge variant="outline" className="text-xs">
                                    {chat.nome_funil}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {chat.estagio_funil ? (
                                  <Badge variant="secondary" className="text-xs">
                                    {chat.estagio_funil}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-gray-700">
                                {chat.chat_data_criacao ? fDateTimeSuffix(new Date(chat.chat_data_criacao)) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

        </div>
      </div>
    </Page>
  );
}
